import { useEffect, useMemo, useState } from "react";
import { Loader2, X, UserPlus, Mail, Search, Eye } from "lucide-react";
import { toast } from "sonner";
import { staffApi, fmtDate, fmtErr } from "../api";

const ROLES = ["Employee", "Manager", "HOD", "HR", "Director", "Admin", "SuperAdmin"];
const EMPTY_FORM = { name: "", email: "", employee_code: "", password: "", role: "Employee", department: "", designation: "", phone: "", grade: "", location: "", date_of_joining: "", primary_branch_id: "", dob: "" };

const DetailField = ({ label, value }) => (
  <div className="border-b border-slate-100 py-2.5">
    <p className="font-mono-tech text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">{label}</p>
    <p className="mt-0.5 text-sm font-medium text-slate-800">{value || "—"}</p>
  </div>
);

const EmployeeMaster = () => {
  const [rows, setRows] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [q, setQ] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [editing, setEditing] = useState(null); // null | "new" | user object
  const [viewing, setViewing] = useState(null); // user object
  const [form, setForm] = useState(EMPTY_FORM);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState("");
  const [inviting, setInviting] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const [u, b] = await Promise.all([staffApi.get("/users"), staffApi.get("/branches")]);
      setRows(Array.isArray(u.data) ? u.data : []);
      setBranches(Array.isArray(b.data) ? b.data : []);
    } catch (e) {
      setError(fmtErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY_FORM); setFormError(""); setEditing("new"); };
  const openEdit = (u) => {
    setForm({
      name: u.name || "", email: u.email || "", employee_code: u.employee_code || "", password: "",
      role: u.role || "Employee", department: u.department || "", designation: u.designation || "",
      phone: u.phone || "", grade: u.grade || "", location: u.location || "",
      date_of_joining: u.date_of_joining || "", primary_branch_id: u.primary_branch_id || "", dob: u.dob || "",
    });
    setFormError("");
    setEditing(u);
  };

  const submit = async (e) => {
    e.preventDefault();
    setFormError("");
    setBusy(true);
    const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== "" && v !== null));
    try {
      if (editing === "new") {
        if (!payload.password || payload.password.length < 8) { setFormError("Password is required (min 8 characters)."); setBusy(false); return; }
        await staffApi.post("/users", payload);
        toast.success("Employee created.");
      } else {
        await staffApi.patch(`/users/${editing.id}`, payload);
        toast.success("Employee updated.");
      }
      setEditing(null);
      await load();
    } catch (err) {
      setFormError(fmtErr(err));
    } finally {
      setBusy(false);
    }
  };

  const toggleActive = async (u) => {
    try {
      await staffApi.patch(`/users/${u.id}`, { active: !(u.active !== false) });
      toast.success(`${u.name} ${u.active === false ? "activated" : "deactivated"}.`);
      await load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const invite = async (u) => {
    setInviting(u.id);
    try {
      await staffApi.post(`/users/${u.id}/invite`, { resend: true });
      toast.success(`Invite sent to ${u.email}.`);
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setInviting(null);
    }
  };

  const departments = useMemo(
    () => [...new Set(rows.map((u) => u.department).filter(Boolean))].sort(),
    [rows]
  );
  const roles = useMemo(
    () => [...new Set([...ROLES, ...rows.map((u) => u.role).filter(Boolean)])],
    [rows]
  );

  const filtered = rows.filter((u) => {
    if (deptFilter && u.department !== deptFilter) return false;
    if (roleFilter && u.role !== roleFilter) return false;
    if (statusFilter === "active" && u.active === false) return false;
    if (statusFilter === "inactive" && u.active !== false) return false;
    const s = `${u.name} ${u.email} ${u.employee_code} ${u.department} ${u.designation}`.toLowerCase();
    return s.includes(q.toLowerCase());
  });

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "";

  const selectCls = "border border-slate-300 px-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]";

  return (
    <div data-testid="employee-master">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Employee Master</h1>
          <p className="mt-1 text-sm text-slate-500">The central employee record — shared with the GR Attendance mobile app. {rows.length > 0 && <span data-testid="emp-count">{rows.length} employees.</span>}</p>
        </div>
        <button onClick={openNew} data-testid="emp-add-btn"
          className="inline-flex items-center gap-1.5 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
          <UserPlus size={13} /> Add Employee
        </button>
      </div>

      <div className="mt-6 flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search name, email, code, department…" value={q} data-testid="emp-search"
            onChange={(e) => setQ(e.target.value)}
            className="w-full border border-slate-300 pl-9 pr-3 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" />
        </div>
        <select value={deptFilter} onChange={(e) => setDeptFilter(e.target.value)} data-testid="emp-filter-dept" className={selectCls}>
          <option value="">All Departments</option>
          {departments.map((d) => <option key={d} value={d}>{d}</option>)}
        </select>
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} data-testid="emp-filter-role" className={selectCls}>
          <option value="">All Roles</option>
          {roles.map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="emp-filter-status" className={selectCls}>
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="inactive">Inactive</option>
        </select>
      </div>

      {loading ? (
        <p className="mt-10 flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>
      ) : error ? (
        <p className="mt-6 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="emp-error">{error}</p>
      ) : (
        <div className="mt-5 bg-white border border-slate-200 overflow-x-auto" data-testid="emp-table">
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-slate-200 text-left">
                {["Employee", "Code", "Role", "Department", "Property", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-4 py-3 font-mono-tech text-[10px] font-bold uppercase tracking-[0.2em] text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((u) => (
                <tr key={u.id} data-testid={`emp-row-${u.id}`}>
                  <td className="px-4 py-3">
                    <p className="font-medium text-slate-800">{u.name}</p>
                    <p className="text-xs text-slate-400">{u.email || "no email"}</p>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{u.employee_code || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{u.role}</td>
                  <td className="px-4 py-3 text-slate-600">{u.department || "—"}</td>
                  <td className="px-4 py-3 text-slate-600" data-testid={`emp-property-${u.id}`}>{branchName(u.primary_branch_id) || "—"}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${u.active !== false ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500 border-slate-200"}`} data-testid={`emp-status-${u.id}`}>
                      {u.active !== false ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <button onClick={() => setViewing(u)} data-testid={`emp-view-${u.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-500 hover:text-[#0A1128] hover:underline"><Eye size={11} /> View</button>
                      <button onClick={() => openEdit(u)} data-testid={`emp-edit-${u.id}`} className="text-[11px] font-semibold text-[#0A1128] hover:underline">Edit</button>
                      {u.email && (
                        <button onClick={() => invite(u)} disabled={inviting === u.id} data-testid={`emp-invite-${u.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#C5221F] hover:underline disabled:opacity-50">
                          {inviting === u.id ? <Loader2 size={11} className="animate-spin" /> : <Mail size={11} />} Invite
                        </button>
                      )}
                      <button onClick={() => toggleActive(u)} data-testid={`emp-toggle-${u.id}`} className="text-[11px] font-semibold text-slate-500 hover:underline">
                        {u.active !== false ? "Deactivate" : "Activate"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-sm text-slate-500" data-testid="emp-empty">No employees match the current filters.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* detail view */}
      {viewing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="emp-detail-modal" onClick={() => setViewing(null)}>
          <div className="bg-white w-full max-w-xl max-h-[85vh] overflow-y-auto p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900" data-testid="emp-detail-name">{viewing.name}</h2>
              <button onClick={() => setViewing(null)} data-testid="emp-detail-close" className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <p className="text-xs text-slate-400">{viewing.designation || viewing.role}{viewing.department ? ` · ${viewing.department}` : ""}</p>
            <div className="mt-4 grid sm:grid-cols-2 gap-x-6">
              <DetailField label="Employee Code" value={viewing.employee_code} />
              <DetailField label="Email" value={viewing.email} />
              <DetailField label="Phone" value={viewing.phone} />
              <DetailField label="Role" value={viewing.role} />
              <DetailField label="Department" value={viewing.department} />
              <DetailField label="Designation" value={viewing.designation} />
              <DetailField label="Grade" value={viewing.grade} />
              <DetailField label="Location" value={viewing.location} />
              <DetailField label="Branch" value={branchName(viewing.primary_branch_id)} />
              <DetailField label="Date of Birth" value={fmtDate(viewing.dob)} />
              <DetailField label="Date of Joining" value={fmtDate(viewing.date_of_joining)} />
              <DetailField label="Status" value={viewing.active !== false ? "Active" : "Inactive"} />
            </div>
            <button onClick={() => { setViewing(null); openEdit(viewing); }} data-testid="emp-detail-edit"
              className="mt-6 w-full bg-[#0A1128] hover:bg-[#16204a] text-white font-display font-semibold py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
              Edit This Employee
            </button>
          </div>
        </div>
      )}

      {/* create/edit */}
      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" data-testid="emp-form-modal" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-lg max-h-[85vh] overflow-y-auto p-6 lg:p-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h2 className="font-display text-lg font-bold text-slate-900">{editing === "new" ? "Add Employee" : `Edit ${editing.name}`}</h2>
              <button onClick={() => setEditing(null)} data-testid="emp-form-close" className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <form onSubmit={submit} className="mt-5 grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Name *</label>
                <input required data-testid="emp-form-name" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Email</label>
                <input type="email" data-testid="emp-form-email" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Employee Code</label>
                <input data-testid="emp-form-code" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Role</label>
                <select data-testid="emp-form-role" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
                  {roles.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Department</label>
                <input data-testid="emp-form-dept" list="emp-dept-list" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} />
                <datalist id="emp-dept-list">{departments.map((d) => <option key={d} value={d} />)}</datalist>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Designation</label>
                <input data-testid="emp-form-desig" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Phone</label>
                <input data-testid="emp-form-phone" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Branch</label>
                <select data-testid="emp-form-branch" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" value={form.primary_branch_id} onChange={(e) => setForm({ ...form, primary_branch_id: e.target.value })}>
                  <option value="">—</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Grade</label>
                <input data-testid="emp-form-grade" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.grade} onChange={(e) => setForm({ ...form, grade: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Location</label>
                <input data-testid="emp-form-location" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
              </div>
              <div>
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">Date of Joining</label>
                <input type="date" data-testid="emp-form-doj" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm bg-white focus:outline-none focus:border-[#C5221F]" value={form.date_of_joining} onChange={(e) => setForm({ ...form, date_of_joining: e.target.value })} />
              </div>
              <div className="col-span-2">
                <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{editing === "new" ? "Password *" : "Reset Password (optional)"}</label>
                <input type="password" autoComplete="new-password" data-testid="emp-form-password" className="w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                {editing !== "new" && <p className="mt-1 text-[11px] text-slate-400">Leave blank to keep unchanged.</p>}
              </div>
              {formError && <p className="col-span-2 text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid="emp-form-error">{formError}</p>}
              <div className="col-span-2">
                <button type="submit" disabled={busy} data-testid="emp-form-submit"
                  className="w-full bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold py-3 text-xs uppercase tracking-widest rounded-sm transition-colors inline-flex items-center justify-center gap-2">
                  {busy && <Loader2 size={14} className="animate-spin" />} {editing === "new" ? "Create Employee" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeMaster;
